/**
 * AIVisualization Component
 * Displays AI voice visualization with dynamic particles
 */

import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useAudioPlayer } from '../../../hooks/useAudioPlayer';
import { DynamicParticles } from './visualizations/DynamicParticles';
import type { VisualizationStyle } from '../../../config/voiceVisualization';

interface AIVisualizationProps {
  onSendMessage: (message: string) => void;
  isPlaying: boolean;
  audioData: string | null;
  audioFormat: string;
  disabled?: boolean;
  size?: 'default' | 'small' | 'xsmall';
  leftButton?: ReactNode;
  rightButton?: ReactNode;
  isMuted?: boolean;
  onAudioPlayerReady?: (player: { stop: () => void; play: (audioData: string, format: string, allowAutoplay?: boolean) => Promise<void> }) => void;
  isRecording?: boolean;
  onStartRecording?: () => Promise<void>;
  onStopRecording?: () => Promise<void>;
  recordingMode?: boolean; // true when inputMode === 'voice'
  visualizationStyle?: VisualizationStyle;
  isLoading?: boolean; // For processing state
}

export function AIVisualization({
  audioData,
  audioFormat,
  size = 'default',
  leftButton,
  rightButton,
  isMuted = false,
  onAudioPlayerReady,
  isRecording = false,
  recordingMode = false,
  isLoading = false,
}: AIVisualizationProps) {
  const { playAudio, isPlaying, stop } = useAudioPlayer();

  // Expose stop and play functions to parent
  useEffect(() => {
    if (onAudioPlayerReady) {
      onAudioPlayerReady({ stop, play: playAudio });
    }
  }, [onAudioPlayerReady, stop, playAudio]);

  // Size-based styling - scaled down for visualizations
  const visualizationSizePx = size === 'xsmall' ? 80 : size === 'small' ? 100 : 120;

  // Determine active state for visualizations - only speed up when AI is speaking
  const isActive = isPlaying; // Only active when AI is speaking
  const showRecordingState = isRecording && recordingMode;
  
  const ariaLabel = showRecordingState
    ? 'Recording... Tap to stop'
    : isPlaying
    ? 'AI is speaking'
    : isLoading
    ? 'Processing your request'
    : recordingMode
    ? 'Tap to start recording'
    : 'Ready to listen';

  // Render selected visualization
  const renderVisualization = () => {
    // Only dynamic-particles is supported now
    return <DynamicParticles isActive={isActive} size={visualizationSizePx} />;
  };

  // Auto-play audio when available - only trigger on audioData change
  // Don't play if muted
  // Note: playAudio will check for user interaction and handle autoplay policy gracefully
  useEffect(() => {
    if (audioData && !isMuted) {
      // Pass allowAutoplay=true for automatic playback from useEffect
      // playAudio will skip if user hasn't interacted yet (avoids NotAllowedError)
      playAudio(audioData, audioFormat, true).catch((err) => {
        // Should not reach here for NotAllowedError (handled in playAudio)
        // Only log other errors
        if (!(err instanceof Error && err.name === 'NotAllowedError')) {
          console.error('[AIVisualization] Failed to play audio:', err);
        }
      });
    } else if (isMuted && isPlaying) {
      // Stop audio if muted while playing
      stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioData, isMuted]); // Re-run when audioData or mute state changes

  return (
    <>
      {/* AI Visualization Container with Optional Side Buttons */}
      <div className="flex items-center justify-center gap-3">
        {/* Left Button */}
        {leftButton && (
          <div className="flex-shrink-0" style={{ minWidth: '44px', minHeight: '44px' }}>
            {leftButton}
          </div>
        )}

        {/* Visualization only - no size constraints, allow overflow for full visibility */}
        <div
          className="relative z-10 flex items-center justify-center"
          style={{
            flexShrink: 0,
            width: `${visualizationSizePx}px`,
            height: `${visualizationSizePx}px`,
            overflow: 'visible', // Allow visualization to extend beyond container if needed
          }}
          aria-label={ariaLabel}
        >
          {renderVisualization()}
        </div>

        {/* Right Button */}
        {rightButton && (
          <div className="flex-shrink-0" style={{ minWidth: '44px', minHeight: '44px' }}>
            {rightButton}
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulseVoice {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 8px 32px rgba(168, 146, 107, 0.5);
          }
          50% {
            transform: scale(1.1);
            box-shadow: 0 12px 48px rgba(168, 146, 107, 0.6);
          }
        }
      `}</style>
    </>
  );
}
